import { Box, Card, CardActionArea, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { ReactElement, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { QueryVehicleManifestItem } from '../../api/schema';
import { categorySummary } from '../../helpers/categoryCounts';
import useAppDispatch from '../../hooks/useAppDispatch';
import { getVehicleManifest, getVehicleManifestFetching, getVehicleManifestSelector } from '../../redux/slices/assignments/assignmentsSlice';

const actions = [
  {
    key: 'pickup',
    label: 'Pickup',
    description: 'Scan an item to pull all equipment for its job',
    icon: 'tags-icon-barcode',
    path: '/field-service/pickup',
  },
  {
    key: 'deliver',
    label: 'Deliver',
    description: 'Scan an item at the job site to drop the whole assignment',
    icon: 'tags-icon-job-site',
    path: '/field-service/deliver',
  },
  {
    key: 'recover',
    label: 'Recover',
    description: 'Scan each item you are taking back from a job',
    icon: 'tags-icon-home',
    path: '/field-service/recover',
  },
];

export const FieldServiceHome = (): ReactElement => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const manifest = useSelector(getVehicleManifestSelector);
  const fetching = useSelector(getVehicleManifestFetching);

  useEffect(() => {
    dispatch(getVehicleManifest({ hideError: true }));
  }, [dispatch]);

  // Picked-up items grouped by the last job site each was seen at.
  const recoveredGroups = useMemo(() => {
    const groups = new Map<string, QueryVehicleManifestItem[]>();
    (manifest?.picked_up ?? []).forEach((item) => {
      const site = item.last_site_name || 'Unknown site';
      groups.set(site, [...(groups.get(site) ?? []), item]);
    });
    return Array.from(groups.entries());
  }, [manifest]);

  const outbound = manifest?.outbound ?? [];
  const truckEmpty = outbound.length === 0 && recoveredGroups.length === 0;

  return (
    <>
      <Helmet title="Field Service" />
      <Typography variant="h4" gutterBottom>
        Field Service
      </Typography>
      <Stack spacing={4}>
        <Typography variant="h6">On Board</Typography>
        {fetching && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {!fetching && truckEmpty && <Typography color="text.secondary">Your truck is empty.</Typography>}
        {!fetching &&
          outbound.map((site) => (
            <Card key={site.assignment_id} sx={{ borderLeft: 4, borderColor: 'primary.main' }}>
              <CardActionArea onClick={() => navigate(`/field-service/deliver/${site.assignment_id}`)} sx={{ p: 4 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
                  <Box>
                    <Typography variant="h6">{site.job_site_name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {[site.job_site_address, site.job_site_city, site.job_site_state].filter(Boolean).join(', ')}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {categorySummary(site.items)}
                    </Typography>
                  </Box>
                  <Chip label="Deliver" color="primary" />
                </Stack>
              </CardActionArea>
            </Card>
          ))}
        {!fetching &&
          recoveredGroups.map(([siteName, items]) => (
            <Card key={siteName} sx={{ borderLeft: 4, borderColor: 'success.main' }}>
              <Box sx={{ p: 4 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
                  <Box>
                    <Typography variant="h6">Recovered from {siteName}</Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {categorySummary(items)}
                    </Typography>
                  </Box>
                  <Chip label="On board" color="success" variant="outlined" />
                </Stack>
              </Box>
            </Card>
          ))}

        <Typography variant="h6" sx={{ pt: 2 }}>
          Actions
        </Typography>
        {actions.map((fn) => (
          <Card key={fn.key}>
            <CardActionArea
              onClick={() => navigate(fn.path)}
              sx={{ p: 4, display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 4, minHeight: 64 }}
            >
              <Box component="i" className={fn.icon} sx={{ fontSize: 28 }} />
              <Box>
                <Typography variant="h6">{fn.label}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {fn.description}
                </Typography>
              </Box>
            </CardActionArea>
          </Card>
        ))}
      </Stack>
    </>
  );
};

export default FieldServiceHome;
